import {useEffect, useState} from "react";
import {Button, Card, Col, message, Row, Statistic, Table} from "antd";
import {PrinterOutlined, ReloadOutlined} from "@ant-design/icons";
import Api from "../../services/Api";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import flexCurrency from "../../utils/FlexCurrency";

dayjs.locale("tr");
dayjs.extend(isSameOrAfter);

const printStyle = `
  .rst-haftalik-table .ant-table-cell { padding: 3px !important; }
  @media print {
    body * { visibility: hidden; }
    #rst-haftalik-print-area, #rst-haftalik-print-area * { visibility: visible; }
    #rst-haftalik-print-area { position: absolute; top: 0; left: 0; width: 100%; }
    .no-print { display: none !important; }
  }
`;

const RENKLER = {
    teras: "#1677ff",
    bufe:  "#fa8c16",
    salon: "#722ed1",
    genel: "#52c41a",
};

const columns = [
    {
        title: "Tarih",
        dataIndex: "Gun",
        render: g => dayjs(g).format("DD MMMM YYYY dddd"),
    },
    {
        title: <span style={{color: RENKLER.teras}}>Teras (₺)</span>,
        dataIndex: "TerasToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.teras}}>{flexCurrency(v)}</span>,
    },
    {
        title: <span style={{color: RENKLER.bufe}}>Büfe (₺)</span>,
        dataIndex: "BufeToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.bufe}}>{flexCurrency(v)}</span>,
    },
    {
        title: <span style={{color: RENKLER.salon}}>Salon (₺)</span>,
        dataIndex: "SalonToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.salon}}>{flexCurrency(v)}</span>,
    },
    {
        title: "Genel Toplam (₺)",
        dataIndex: "GenelToplam",
        className: "text-align-right",
        render: v => <b style={{color: RENKLER.genel}}>{flexCurrency(v)}</b>,
    },
];

const groupByMonth = (items) => {
    const groups = {};
    items.forEach(item => {
        const ay = dayjs(item.Gun).format("YYYY-MM");
        if (!groups[ay]) groups[ay] = [];
        groups[ay].push(item);
    });

    return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map(ay => {
            const gunler = [...groups[ay]].sort(
                (a, b) => new Date(b.Gun) - new Date(a.Gun)
            );
            const total = {
                TerasToplam: gunler.reduce((s, i) => s + i.TerasToplam, 0),
                BufeToplam:  gunler.reduce((s, i) => s + i.BufeToplam,  0),
                SalonToplam: gunler.reduce((s, i) => s + i.SalonToplam, 0),
                GenelToplam: gunler.reduce((s, i) => s + i.GenelToplam, 0),
            };
            const label = dayjs(`${ay}-01`).format("MMMM YYYY");
            return {ay, label, gunler, total};
        });
};

/* ── Özet şeridi ────────────────────────────────────────── */
const OzetSerit = ({total}) => (
    <Row
        gutter={[16, 8]}
        style={{padding: "10px 16px", background: "#fafafa", borderBottom: "1px solid #f0f0f0"}}
    >
        <Col xs={12} sm={6}>
            <Statistic
                title="Teras"
                value={total.TerasToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.teras}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Büfe"
                value={total.BufeToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.bufe}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Salon"
                value={total.SalonToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.salon}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Toplam Tutar"
                value={total.GenelToplam}
                suffix="₺"
                valueStyle={{fontSize: 14, color: RENKLER.genel, fontWeight: 700}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
    </Row>
);

/* ── Haftalık özet kartı ────────────────────────────────── */
const HaftalikOzet = ({toplam}) => (
    <Card
        style={{
            marginBottom: 12,
            background: "linear-gradient(135deg, #f6ffed 0%, #e6f4ff 100%)",
            border: "1px solid #91caff",
        }}
        bodyStyle={{padding: "12px 16px"}}
    >
        <Row align="middle" gutter={[24, 8]}>
            <Col xs={24} sm={4}>
                <div style={{fontSize: 16, fontWeight: 800, color: "#1677ff"}}>Son 7 Gün</div>
                <div style={{fontSize: 11, color: "#888", marginTop: 2}}>Haftalık Özet</div>
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Teras"
                    value={toplam.TerasToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.teras, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Büfe"
                    value={toplam.BufeToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.bufe, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Salon"
                    value={toplam.SalonToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.salon, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Genel Toplam"
                    value={toplam.GenelToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 16, color: RENKLER.genel, fontWeight: 800}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
        </Row>
    </Card>
);

/* ── Ana bileşen ────────────────────────────────────────── */
const RestaurantHaftalikOzetRapor = () => {
    const [monthData, setMonthData] = useState([]);
    const [haftalikToplam, setHaftalikToplam] = useState(null);
    const [loading, setLoading] = useState(false);

    const getData = () => {
        setLoading(true);
        Api.get("api/ToyaPos/GunlukOzet")
            .then(res => {
                const baslangic = dayjs().subtract(6, "day").startOf("day");
                const son7Gun = res.data.filter(item =>
                    dayjs(item.Gun).isSameOrAfter(baslangic)
                );

                const gruplu = groupByMonth(son7Gun);
                setMonthData(gruplu);

                setHaftalikToplam({
                    TerasToplam: son7Gun.reduce((s, i) => s + i.TerasToplam, 0),
                    BufeToplam:  son7Gun.reduce((s, i) => s + i.BufeToplam,  0),
                    SalonToplam: son7Gun.reduce((s, i) => s + i.SalonToplam, 0),
                    GenelToplam: son7Gun.reduce((s, i) => s + i.GenelToplam, 0),
                });
            })
            .catch(() => message.error("Veriler alınırken hata oluştu."))
            .finally(() => setLoading(false));
    };

    useEffect(() => { getData(); }, []);

    return (
        <div className={"p-1"}>
            <style>{printStyle}</style>

            {/* Üst bar */}
            <Card
                className={"no-print"}
                style={{marginBottom: 12}}
                bodyStyle={{padding: "10px 16px"}}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <span style={{fontWeight: 700, fontSize: 15}}>Restaurant Haftalık Özet Raporu</span>
                    </Col>
                    <Col>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={getData}
                            loading={loading}
                            style={{marginRight: 8}}
                        >
                            Yenile
                        </Button>
                        {monthData.length > 0 && (
                            <Button icon={<PrinterOutlined/>} onClick={() => window.print()}>
                                Yazdır
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card>

            {loading && <Card loading style={{marginBottom: 12}}/>}

            <div id={"rst-haftalik-print-area"}>
                {haftalikToplam && <HaftalikOzet toplam={haftalikToplam}/>}

                {monthData.map(({ay, label, gunler, total}) => (
                    <Card
                        key={ay}
                        size={"small"}
                        title={<span style={{fontWeight: 600}}>{label}</span>}
                        bodyStyle={{padding: 0}}
                        style={{marginBottom: 8}}
                    >
                        <OzetSerit total={total}/>
                        <Table
                            className={"rst-haftalik-table"}
                            size={"small"}
                            bordered
                            dataSource={gunler}
                            rowKey={r => r.Gun}
                            columns={columns}
                            pagination={false}
                        />
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default RestaurantHaftalikOzetRapor;
